import { createSelector } from '@ngrx/store';
import { Product, ProductOrderHistory } from '../../core/models/product';

import { RequestWrap } from '../../core/models/request-wrap';
import { FilterItem } from '../../shared/models/filter';

import * as product from '../actions/product';
import { Entities, generateEntities } from '../../core/helpers/entities';
import { FavoriteProduct } from '../models/favorite-product';
import { initDomAdapter } from '@angular/platform-browser/src/browser';


export interface State {
  error: any
  ids: string[]
  products: { [id: string]: Product }
  favorites: FavoriteProduct[]
  selectedProduct: Product | string
  productOrderHistory: ProductOrderHistory[]
  hasNext: boolean
  nextRequestId: any
  currentPage: number
  nextPage: number
  totalPages: number
  expanding: boolean
  loading: boolean
  loaded: boolean
  onlyInStock: boolean
  total: number
  productFilters: FilterItem[]
  productSortOrder: string
}

const initialState: State = {
  error: null,
  ids: [],
  products: {},
  favorites: [],
  selectedProduct: null,
  productOrderHistory: [],
  hasNext: true,
  nextRequestId: null,
  currentPage: 1,
  nextPage: 2,
  totalPages: 1,
  expanding: false,
  loading: false,
  loaded: false,
  onlyInStock: false,
  total: 0,
  productFilters: [],
  productSortOrder: null,
};

export function reducer(
  state = initialState,
  action: product.Actions
): State {
  let wrap: RequestWrap<Product[]>
  let generatedData: Entities<Product>

  switch (action.type) {
    case product.FILTER:
      return {
        ...state,
        ids: [],
        products: {},
        total: null,
        loading: true,
        loaded: false
      };

    case product.FILTER_SUCCESS:
      wrap = action.payload;
      generatedData = generateEntities(wrap);

      return {
        ...state,
        loading: false,
        loaded: true,
        ids: generatedData.ids,
        products: generatedData.entities,
        selectedProduct: null,
        currentPage: 1,
        nextPage: 2,
        totalPages: generatedData.totalPages,
        total: generatedData.totalCount
      };

    case product.FILTER_FAILED:
      return {
        ...state,
        loading: false,
        loaded: false
      };

    case product.CLEAR_STATE:
      return Object.assign({}, initialState)

    case product.GET_FAVORITES:
      return {
        ...state
      }

    case product.GET_FAVORITES_SUCCESS:
      return {
        ...state,
        favorites: action.payload
      }

    case product.GET_FAVORITES_FAILED:
      return {
        ...state,
        error: action.payload,
      }

    case product.ADD_FAVORITE:
      return {
        ...state
      }

    case product.ADD_FAVORITE_SUCCESS:
      return {
        ...state,
        favorites: [...state.favorites, action.payload],
        loading: false
      }

    case product.ADD_FAVORITE_FAILED:
      return {
        ...state,
        error: action.payload,
        loading: false
      }

    case product.REMOVE_FAVORITE:
      return {
        ...state
      }

    case product.REMOVE_FAVORITE_SUCCESS:
      const modifiedFavoritesList = [...state.favorites]
      modifiedFavoritesList.splice(state.favorites.indexOf(state.favorites.find(item => item.productCode === action.payload)), 1)

      return {
        ...state,
        favorites: modifiedFavoritesList,
        loading: false
      }

    case product.REMOVE_FAVORITE_FAILED:
      return {
        ...state,
        error: action.payload,
        loading: false
      }

    case product.EXPAND: {
      if ((state.total === state.ids.length) || !state.total) {
        return {
          ...state,
          expanding: false
        }
      } else {
        return {
          ...state,
          expanding: true
        }
      }
    }
    case product.EXPAND_SUCCESS:
      wrap = action.payload;
      generatedData = generateEntities(wrap);

      return {
        ...state,
        ids: [...state.ids, ...generatedData.ids],
        products: {...state.products, ...generatedData.entities},
        currentPage: ++state.currentPage,
        nextPage: ++state.nextPage,
        expanding: false,
      };

    case product.EXPAND_FAILED:
      return {
        ...state,
        expanding: false
      };

    case product.SELECT:
      return {
        ...state,
        selectedProduct: action.payload
      };

    case product.GET_PRODUCT_HISTORY_SUCCESS:
      return {
        ...state,
        productOrderHistory: action.payload
      }

    case product.GET_PRODUCT_HISTORY_FAILED:
      return {
        ...state,
        error: action.payload
      }

    case product.ADD:
      return {
        ...state,
        loading: true
      };

    case product.ADD_SUCCESS:
      let addId = action.payload._id;

      return {
        ...state,
        loading: false,
        ids: [...state.ids, addId],
        products: {
          ...state.products,
          [addId]: action.payload
        }
      };
    case product.ADD_FAILED:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    case product.REMOVE:
      const modifiedProducts = {...state.products}
      delete modifiedProducts[action.payload]
      const modifiedIds = [...state.ids]
      modifiedIds.splice(state.ids.indexOf(action.payload), 1)

      return {
        ...state,
        ids: modifiedIds,
        products: modifiedProducts,
        total: state.total > 0 ? state.total - 1 : 0
      }

    case product.TOGGLE_INSTOCK:
      return {
        ...state,
        onlyInStock: !state.onlyInStock
      };

    case product.SET_PRODUCT_FILTER:
      let productFilters = state.productFilters

      if (productFilters.findIndex((filterToggleEvent) => filterToggleEvent.code === action.payload.code) > -1) {
        productFilters = state.productFilters.filter((filterToggleEvent) => filterToggleEvent.code !== action.payload.code)
      } else {
        const filterItem: FilterItem = {
          code: action.payload.code
        }
        productFilters = [...state.productFilters, filterItem]
      }

      return {
        ...state,
        productFilters: productFilters
      }

    case product.SET_PRODUCT_SORT_ORDER:
    case product.SET_PRODUCT_DEFAULT_SORT_ORDER_SUCCESS:
      return {
        ...state,
        productSortOrder: action.payload,
      }

    default:
      return state;
  }
}

export const getProductState = (state: State) => state;
export const findProducts = (state: State) => state.products;
export const findIds = (state: State) => state.ids;
export const findSelectedId = (state: State) => typeof state.selectedProduct === 'string'
  ? state.selectedProduct
  : state && state.selectedProduct && state.selectedProduct._id
export const findNextRequestId = (state: State) => state.nextRequestId;
export const hasNext = (state: State) => state.hasNext;
export const isLoading = (state: State) => state.loading;
export const isLoaded = (state: State) => state && state.loaded;
export const isExpanding = (state: State) => state && state.expanding;
export const onlyInStock = (state: State) => state.onlyInStock;
export const total = (state: State) => state.total;
export const getProductOrderHistory = (state: State) => state.productOrderHistory
export const getProductFilters = (state: State) => state.productFilters
export const getProductSortOrder = (state: State) => state.productSortOrder

export const getSelected = createSelector(
  findProducts,
  findSelectedId,
  (products, selected) => products[selected]
);

export const getAll = createSelector(
  findProducts,
  findIds,
  (products, ids) => ids.map(id => products[id])
);

export const getProductCount = createSelector(
  findIds,
  ids => ids.length
);

export const getNextPageNumber = createSelector(
  getProductState,
  state => state.nextPage
);

export const getFavorites = createSelector(
  getProductState,
  (state) => state && state.favorites
)
